import React, { useEffect, useState } from 'react';
import { useCore } from 'contexts/core';
import { isRoomConfigurationActive, setRoomConfigurationActive } from 'ducks/layout';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AppRoutes } from 'routes';
import Box from 'ui/Box';
import Slider from 'ui/Slider/Slider';
import { cmToMm, inchToMm, mmToCm, mmToInch } from 'utils/measurementUtils';
import { getRange } from 'utils/queryUtils';
import { getMinHeightForSolution } from './utils';
import { reportRoomSizeChange, reportToggleRoomSize } from 'analytics/reporters';
import { useAppDispatch } from 'ducks/hooks';
import { invalidateVpcCode } from 'ducks/vpc';
import * as R from 'ramda';
import useDebounce from 'hooks/useDebounce';
import { useSceneHandler } from 'contexts/sceneHandler';
import { useTranslations } from 'hooks/useTranslations';
import { resetDesignId } from 'ducks/designId';

/**
 * Defaults in mm
 */
const MIN_HEIGHT: number = getRange() === 'pax' ? 2020 : 2000;
const MAX_HEIGHT: number = 4000;

const KvadratRoomConfigurationMenu = () => {
  const coreApi = useCore();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const isActive = useSelector(isRoomConfigurationActive);
  const { setLocalScene } = useSceneHandler();
  const { unit, useMetric } = useTranslations();
  const { formatMessage } = useIntl();
  const fromMm = useMetric ? mmToCm : mmToInch;
  const toMm = useMetric ? cmToMm : inchToMm;

  const initialHeight = coreApi.kvadratRoom?.kvadrat?.getCeilingHeight();
  const [height, setHeight] = useState(fromMm(initialHeight ? initialHeight : 2600));
  const debouncedRoomDimensions: number = useDebounce<number>(height, 200);

  useEffect(() => {
    if (isActive) {
      coreApi.kvadratRoom?.openRoomEditor();
    } else {
      // Only send the event when the menu is activated and not when closing the menu
      reportToggleRoomSize();
    }

    return () => {
      coreApi.emit.onRoomSize({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  useEffect(() => {
    const { location } = history;
    if (location.pathname !== AppRoutes.PLANNER) {
      dispatch(setRoomConfigurationActive(false));
    }
  }, [dispatch, history]);

  // Triggers when the dimensions is changed
  useEffect(() => {
    if (isActive) {
      const heightInMm = toMm(height);

      const ceilingHeight = coreApi.kvadratRoom?.kvadrat?.getCeilingHeight();
      const oldCeilingHeight = ceilingHeight ? toMm(ceilingHeight) : null;

      if (oldCeilingHeight !== heightInMm) {
        coreApi.kvadratRoom?.kvadrat?.setCeilingHeight(heightInMm);
      }

      setLocalScene();

      if (!R.equals(oldCeilingHeight, heightInMm)) {
        dispatch(invalidateVpcCode());
        dispatch(resetDesignId());
      }
    }
  }, [coreApi, debouncedRoomDimensions, isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSizeChange = (value: number) => {
    if (!value || value === 0 || isNaN(value)) {
      return;
    }

    setHeight(value);
  };

  const outerMeasurements = coreApi.entities.getOuterMeasurements();

  const minHeight = outerMeasurements?.height
    ? Math.ceil(fromMm(getMinHeightForSolution(coreApi, outerMeasurements.height)))
    : fromMm(MIN_HEIGHT, true);
  const maxHeight = fromMm(MAX_HEIGHT, true);

  const commonStrings = {
    min: formatMessage({ id: 'storageone.minValue' }),
    max: formatMessage({ id: 'storageone.maxValue' }),
    unit
  };
  const heightStrings = Object.assign({}, commonStrings, {
    label: formatMessage({ id: 'storageone.height' }),
    errorBelowMin: formatMessage({ id: 'storageone.errorBelowMinHeight' }),
    errorAboveMax: formatMessage({ id: 'storageone.errorAboveMaxHeight' })
  });

  return (
    <>
      {' '}
      <Box mt="xxl">
        <Slider
          my="xxl"
          strings={heightStrings}
          min={minHeight}
          max={maxHeight}
          value={Math.round(height)}
          onChange={value => onSizeChange(value)}
          onChangeEnd={() => reportRoomSizeChange({ dimension: 'y' })}
        />
      </Box>
    </>
  );
};

export default KvadratRoomConfigurationMenu;
